import { VuexModule, Module, Mutation, Action, getModule, MutationAction } from 'vuex-module-decorators';
import store from '@/store';
import {
    GroupChatViewObject,
    UpdateGroupChatNameRequest,
    KickGroupChatMemberRequest,
    ChatMember,
    ChatMessageViewObject,
    ReactionType,
    UpdateGroupChatImageRequest
} from '@/types/apiServerContract';
import { ChatApi } from '@/services/api/api.chat';

@Module({ namespaced: true, dynamic: true, store, name: 'ChatModule' })
class ChatState extends VuexModule {
	chats: GroupChatViewObject[] = [];
	messages: ChatMessageViewObject[] = [];
	activeChat: GroupChatViewObject | null = null;

	get activeChats() {
	    return this.chats.sort((a: GroupChatViewObject, b: GroupChatViewObject) => {
	        return new Date(b.modified).getTime() - new Date(a.modified).getTime();
	    });
	}

	get unreadMessageCount() {
	    return this.chats.map(x => x.unreadMessageCount).reduce((a, b) => a + b, 0);
	}

	@Mutation
	SetActiveChat(chat: GroupChatViewObject | null) {
	    this.messages = [];
	    this.activeChat = chat;
	}

	@Mutation
	AddGroupChat(chat: GroupChatViewObject) {
	    this.chats.unshift(chat);
	}

	@Mutation
	AddMessages(data: { chatId: string; messages: ChatMessageViewObject[] }) {
	    this.messages.unshift(...data.messages);
	}

	@Mutation
	GroupChatUpdated(chatId: string) {
	    const chat = this.chats.find(x => x.id === chatId);
	    if (chat) {
	        chat.modified = new Date();
	    }
	}

	@Mutation
	ClearMessages() {
	    this.messages = [];
	}

	@Mutation
	RemoveGroupChat(chatId: string) {
	    const index = this.chats.findIndex(x => x.id === chatId);
	    if (index >= 0) {
	        this.chats.splice(index, 1);
	    }
	}

	@Mutation
	UpdateGroupChat(chat: GroupChatViewObject) {
	    const oldChat = this.chats.find(x => x.id === chat.id);
	    Object.assign(oldChat, chat);
	}

	@Mutation
	MarkMessagesAsReadForChat(chatId: string) {
	    const chat = this.chats.find(x => x.id === chatId);
	    if (chat) {
	        chat.unreadMessageCount = 0;
	    }
	}

	@Mutation
	GroupChatNameChanged(data: { id: string; name: string }) {
	    const chat = this.chats.find(x => x.id === data.id);
	    if (chat) {
	        chat.name = data.name;
	        chat.modified = new Date();
	    }
	}

	@Mutation
	GroupChatImageChanged(data: { id: string; image: string }) {
	    const chat = this.chats.find(x => x.id === data.id);
	    if (chat) {
	        chat.image = data.image;
	        chat.modified = new Date();
	    }
	}

	@Mutation
	AddChatMember(data: { id: string; member: ChatMember }) {
	    const chat = this.chats.find(x => x.id === data.id);
	    if (chat) {
	        chat.members.push(data.member);
	        chat.modified = new Date();
	    }
	}

	@Mutation
	RemoveChatMember(data: { id: string; member: ChatMember }) {
	    const chat = this.chats.find(x => x.id === data.id);
	    if (chat) {
	        chat.members.splice(
	            chat.members.findIndex(x => x.author === data.member.author),
	            1
	        );
	        chat.modified = new Date();
	    }
	}

	@Mutation
	GroupChatMemberConnected(data: { groupId: string; memberId: string }) {
	    const chat = this.chats.find(x => x.id === data.groupId);
	    if (chat) {
	        const member = chat.members.find(x => x.author === data.memberId);
	        if (member) {
	            member.online = true;
	        }
	    }
	}

	@Mutation
	GroupChatMemberDisconnected(data: { groupId: string; memberId: string }) {
	    const chat = this.chats.find(x => x.id === data.groupId);
	    if (chat) {
	        const member = chat.members.find(x => x.author === data.memberId);
	        if (member) {
	            member.online = false;
	        }
	    }
	}

	@Mutation
	GroupChatMessageReceived(message: ChatMessageViewObject) {
	    this.messages.unshift(message);
	    const chat = this.chats.find(x => x.id === message.groupId);
	    if (!chat) return;
	    if (this.activeChat?.id !== chat.id) {
	        chat.unreadMessageCount++;
	    }
	    chat.modified = new Date();
	}

	@Mutation
	GroupChatMessageReaction(data: { messageId: string; author: string; authorName: string; type: ReactionType; active: boolean }) {
	    const message = this.messages.find(x => x.id === data.messageId);
	    if (message) {
	        if (!data.active) {
	            message.reactions.push({
	                active: true,
	                author: data.author,
	                authorEmail: '',
	                authorName: data.authorName,
	                emailHidden: false,
	                type: data.type
	            });
	        } else {
	            message.reactions.splice(
	                message.reactions.findIndex(x => x.type === data.type && x.author === data.author),
	                1
	            );
	        }
	    }
	}

	@MutationAction({ mutate: ['chats'], rawError: true })
	async GetGroupChats() {
	    const result = await ChatApi.GetGroupChats();
	    return { chats: result.groupChats };
	}

	@Action({ rawError: true })
	async GetMessagesForChat(data: { chatId: string; page: number; requestedMessageId?: string | null }) {
	    const result = await ChatApi.GetMessagesForChat(data.chatId, data.page, data.requestedMessageId);
	    this.AddMessages({ chatId: data.chatId, messages: result.messages });
	    return result;
	}

	@Action({ rawError: true })
	async LeaveGroupChat(chatId: string) {
	    const result = await ChatApi.LeaveGroupChat(chatId);
	    this.RemoveGroupChat(chatId);
	    this.SetActiveChat(null);
	    return result.data;
	}

	@Action({ rawError: true })
	async MarkMessagesAsRead(chatId: string) {
	    await ChatApi.MarkMessagesAsRead(chatId);
	    this.MarkMessagesAsReadForChat(chatId);
	}

	@Action({ rawError: true })
	async UpdateGroupChatImage(request: UpdateGroupChatImageRequest) {
	    await ChatApi.UpdateGroupChatImage(request);
	}

	@Action({ rawError: true })
	async UpdateGroupChatName(request: UpdateGroupChatNameRequest) {
	    await ChatApi.UpdateGroupChatName(request);
	}

	@Action({ rawError: true })
	async KickMember(request: KickGroupChatMemberRequest) {
	    await ChatApi.KickMember(request);
	}

	@Mutation
	Reset() {
	    this.activeChat = null;
	    this.messages = [];
	}

	@Action({ rawError: true })
	Clear() {
	    this.Reset();
	}

	@Action({ rawError: true })
	async Init() {}
}

const ChatModule = getModule(ChatState);

export { ChatModule };
